import { isAxiosError } from 'services/errors';

import type { ErrorSource, NormalisedCommandError, ServiceCommandError } from './types';

const CLIENT_TIMEOUT_STATUS = 504;
const AXIOS_TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];
const AXIOS_TIMEOUT_MESSAGE = /timeout of \d+ms exceeded/;

const isAxiosTimeout = (error: { code?: string; message?: string }): boolean =>
    (error.code !== undefined && AXIOS_TIMEOUT_CODES.includes(error.code)) ||
    (error.message !== undefined && AXIOS_TIMEOUT_MESSAGE.test(error.message));

const errorCodeOf = (data: unknown): string | undefined => {
    if (typeof data !== 'object' || data === null) {
        return;
    }
    const { code } = data as { code?: unknown };
    return typeof code === 'string' ? code : undefined;
};

const errorSourceOf = (data: unknown): ErrorSource => {
    if (typeof data !== 'object' || data === null) {
        return 'UNKNOWN';
    }
    const { origin } = data as { origin?: unknown };
    return origin === 'ONBOARD' || origin === 'OFFBOARD' ? origin : 'UNKNOWN';
};

const restErrorStatusOf = (error: unknown): number | undefined => {
    if (!(error instanceof Error)) {
        return;
    }
    const { status } = error as Error & { status?: unknown };
    return typeof status === 'number' ? status : undefined;
};

const isServiceCommandError = (error: unknown): error is ServiceCommandError => {
    if (typeof error !== 'object' || error === null) {
        return false;
    }
    const { source } = error as { source?: unknown };
    return source === 'ONBOARD' || source === 'OFFBOARD' || source === 'UNKNOWN';
};

type CommandErrorFacts = Omit<ServiceCommandError, 'message'>;

const commandErrorFactsOf = (error: unknown): CommandErrorFacts => {
    if (isServiceCommandError(error)) {
        return { status: error.status, code: error.code, source: error.source };
    }

    if (isAxiosError(error)) {
        return {
            status: error.response?.status ?? (isAxiosTimeout(error) ? CLIENT_TIMEOUT_STATUS : undefined),
            code: errorCodeOf(error.response?.data),
            source: errorSourceOf(error.response?.data),
        };
    }

    return { status: restErrorStatusOf(error), code: undefined, source: 'UNKNOWN' };
};

export const normaliseCommandError = (error: unknown, message: string): ServiceCommandError => ({
    ...commandErrorFactsOf(error),
    message,
});

export const normaliseCommandRejection = <C extends string>(error: unknown, command: C): NormalisedCommandError<C> => ({
    ...commandErrorFactsOf(error),
    command,
});
